/**
 * authHelpers.js — Helpers d'auth standalone (sans AWS Amplify)
 *
 * Remplace l'ancien pattern `await fetchAuthSession()` d'@aws-amplify/auth.
 * Lit directement les tokens stockés par AuthContext dans expo-secure-store.
 *
 * - getAuthToken() : retourne l'idToken courant, ou null si absent/expiré.
 * - Ne gère PAS le refresh automatique (pour ça utilisez AuthContext.getValidToken).
 *   Mais comme AuthContext rafraîchit en arrière-plan au démarrage et avant
 *   chaque action utilisateur critique, la lecture directe du SecureStore
 *   renvoie un token valide dans l'écrasante majorité des cas.
 */
// v1.12.117 — wrapper avec fallback localStorage sur web
import * as SecureStore from '../utils/secureStore';

/**
 * Décode un JWT sans vérifier sa signature (juste pour lire exp).
 */
function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1];
    const json = atob(
      base64.replace(/-/g, '+').replace(/_/g, '/')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/**
 * Retourne l'idToken Cognito stocké dans SecureStore.
 * Retourne null si aucun token ou si le token est expiré.
 *
 * Priorité : idToken (contient les claims email/name/picture attendus
 * par le backend) > accessToken (fallback).
 */
export async function getAuthToken() {
  try {
    const idTok = await SecureStore.getItemAsync('outfit_id');
    if (idTok) {
      const payload = decodeJwt(idTok);
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp > now + 30) {
        return idTok;
      }
    }
    // idToken absent ou expiré → fallback sur accessToken
    const access = await SecureStore.getItemAsync('outfit_access');
    if (access) {
      const payload = decodeJwt(access);
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp > now + 30) {
        return access;
      }
    }
    return null;
  } catch (e) {
    console.warn('[authHelpers] getAuthToken:', e.message);
    return null;
  }
}

/**
 * Retourne l'userId (sub Cognito) du token stocké, ou null.
 * Utile pour générer des clés AsyncStorage préfixées par uid.
 */
export async function getUserIdFromToken() {
  try {
    const tok = await getAuthToken();
    if (!tok) return null;
    const payload = decodeJwt(tok);
    return payload?.sub || null;
  } catch {
    return null;
  }
}

export default { getAuthToken, getUserIdFromToken };
